// v2.18.0 — Page de connexion livreur PRO (token CIV-PRO-XXXXXXXX-XXXX).
// Au succès : redirige vers ?next=... ou /livreur/navigate.

import { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function CourierLoginPage() {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(true);

  // Si déjà connecté, redirige direct.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/v3/courier/me`, { withCredentials: true });
        if (r.data?.ok) {
          const sp = new URLSearchParams(window.location.search);
          const next = sp.get("next") || "/livreur/navigate";
          window.location.replace(next);
          return;
        }
      } catch (_) { /* not logged in — show form */ }
      setChecking(false);
    })();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/v3/courier/login`, { token: token.trim() }, { withCredentials: true });
      if (r.data?.ok) {
        const sp = new URLSearchParams(window.location.search);
        const next = sp.get("next") || "/livreur/navigate";
        window.location.replace(next);
      } else {
        setError("Réponse inattendue du serveur.");
      }
    } catch (err) {
      const code = err.response?.data?.error;
      const map = {
        token_required: "Saisis ton token PRO.",
        invalid_token: "Token introuvable. Vérifie qu'il est bien copié.",
        token_expired: "Ce token PRO a expiré. Contacte ton entreprise.",
        token_revoked: "Ce token PRO a été révoqué.",
        token_inactive: "Ce token PRO n'est pas actif.",
      };
      setError(map[code] || `Erreur : ${code || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div style={styles.bg}>
        <div style={{ color: "#94A3B8", fontSize: 14 }}>Chargement…</div>
      </div>
    );
  }

  return (
    <div style={styles.bg} data-testid="courier-login-page">
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.flag}>🇨🇮</div>
          <div>
            <div style={styles.brand}>JSONFIRST CIV AI</div>
            <div style={styles.title}>Accès Pro Livreur</div>
          </div>
          <span style={styles.proBadge} data-testid="pro-badge">PRO</span>
        </div>

        <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <label style={styles.label}>
            Token PRO
            <input
              type="text"
              data-testid="courier-token-input"
              value={token}
              onChange={(e) => setToken(e.target.value.toUpperCase())}
              placeholder="CIV-PRO-XXXXXXXX-XXXX"
              autoComplete="off"
              spellCheck={false}
              style={styles.input}
              disabled={loading}
            />
          </label>

          {error && (
            <div data-testid="courier-login-error" style={styles.errorBox}>{error}</div>
          )}

          <button
            type="submit"
            data-testid="courier-login-submit"
            disabled={loading || !token.trim()}
            style={{
              ...styles.btn,
              opacity: loading || !token.trim() ? 0.6 : 1,
              cursor: loading || !token.trim() ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Connexion…" : "Se connecter"}
          </button>
        </form>

        <div style={styles.note}>
          Token fourni par votre entreprise après signature du contrat partenaire CIV-AI.
          Le navigateur PRO inclut routing OSRM, alertes trafic, badge livreur et historique de courses.
        </div>

        <div style={styles.footer}>
          <a href="/" style={styles.footerLink}>← Retour à l'accueil</a>
          <a href="/navigate" style={styles.footerLink}>Navigateur public →</a>
        </div>
      </div>
    </div>
  );
}

const styles = {
  bg: {
    minHeight: "100vh",
    width: "100%",
    background: "radial-gradient(ellipse at top, #1E293B 0%, #0F172A 60%, #020617 100%)",
    color: "#E2E8F0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: 440,
    background: "rgba(15, 23, 42, 0.85)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(245, 158, 11, 0.25)",
    borderRadius: 16,
    padding: "28px 26px",
    boxShadow: "0 24px 60px rgba(0,0,0,0.55), 0 0 40px rgba(245,158,11,0.08)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 22,
    position: "relative",
  },
  flag: { fontSize: 36, lineHeight: 1 },
  brand: { fontSize: 11, color: "#94A3B8", letterSpacing: "0.12em", textTransform: "uppercase" },
  title: { fontSize: 20, fontWeight: 700, color: "#F1F5F9", marginTop: 2 },
  proBadge: {
    position: "absolute",
    top: 0,
    right: 0,
    background: "linear-gradient(135deg, #F59E0B, #FBBF24)",
    color: "#0F172A",
    fontSize: 11,
    fontWeight: 800,
    padding: "3px 9px",
    borderRadius: 999,
    boxShadow: "0 2px 8px rgba(245,158,11,0.4)",
  },
  label: { fontSize: 12, color: "#94A3B8", display: "flex", flexDirection: "column", gap: 6 },
  input: {
    padding: "12px 14px",
    fontSize: 14,
    fontFamily: "JetBrains Mono, Menlo, monospace",
    letterSpacing: "0.04em",
    background: "rgba(15, 23, 42, 0.95)",
    border: "1px solid rgba(245, 158, 11, 0.35)",
    color: "#F8FAFC",
    borderRadius: 8,
    outline: "none",
  },
  errorBox: {
    padding: "10px 12px",
    background: "rgba(239, 68, 68, 0.12)",
    border: "1px solid rgba(239, 68, 68, 0.4)",
    color: "#FCA5A5",
    fontSize: 12,
    borderRadius: 8,
  },
  btn: {
    padding: "12px 18px",
    background: "linear-gradient(135deg, #F59E0B, #FBBF24)",
    color: "#0F172A",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.02em",
  },
  note: {
    marginTop: 18,
    fontSize: 11,
    color: "#94A3B8",
    lineHeight: 1.55,
    borderTop: "1px solid rgba(255,255,255,0.06)",
    paddingTop: 14,
  },
  footer: {
    marginTop: 18,
    display: "flex",
    justifyContent: "space-between",
    fontSize: 11,
  },
  footerLink: {
    color: "#94A3B8",
    textDecoration: "none",
    borderBottom: "1px dashed rgba(148,163,184,0.4)",
    paddingBottom: 2,
  },
};
